import DesignButton from 'components/button/DesignButton';
import InfoBox from 'components/infoBox/InfoBox';
import { AlertIcon, InfoIcon } from 'components/svgs';
import { CapacityPlanRunDetail } from 'core/graphql/CapacityPlanning/models';
import { useUpdateCapacityPlanRunSettingsMutation } from 'core/graphql/CapacityPlanning/UpdateCapacityPlanRunSettings.generated';
import { CapacityPlanRunStatus } from 'core/graphql/types';
import useAppDispatch from 'core/hooks/useAppDispatch';
import useAppSelector from 'core/hooks/useAppSelector';
import {
  selectSettingsDraft,
  setSettingsDraft,
} from 'core/store/CapacityPlanningSlice';
import { toast } from 'react-toastify';
import { countChanges, draftToSettings, runToDraft } from './RunSettings';
import RunSettingsForm from './RunSettingsForm';

export interface SettingsTabProps {
  run: CapacityPlanRunDetail;
  canEdit: boolean;
}

/**
 * Run Settings editor of an open Run. Saving is refused by the server while
 * the Run is queued or computing, and on a computed Run it discards the
 * Result and puts the Run back to Ready to compute.
 */
const SettingsTab = ({ run, canEdit }: SettingsTabProps) => {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectSettingsDraft);
  const [updateRunSettings, { loading: isSaving }] =
    useUpdateCapacityPlanRunSettingsMutation({
      refetchQueries: ['CapacityPlanRun', 'CapacityPlanRuns'],
    });

  const isBusy =
    run.status === CapacityPlanRunStatus.Queued ||
    run.status === CapacityPlanRunStatus.Computing;
  const isComputed = run.status === CapacityPlanRunStatus.Computed;
  const isDisabled = !canEdit || isBusy;

  const savedDraft = runToDraft(run.settings, run.label);
  const changes = countChanges(draft, savedDraft);

  const handleSave = async () => {
    const converted = draftToSettings(draft, {
      isNetworkRun: run.isNetworkRun,
    });
    if (!converted.ok) {
      toast.error(converted.message);
      return;
    }

    try {
      const { data } = await updateRunSettings({
        variables: { input: { runId: run.id, settings: converted.settings } },
      });
      // A rejected save (run computing, validation, forbidden) comes back in
      // the payload rather than as a thrown error, so it must be read here.
      const errors = data?.updateCapacityPlanRunSettings?.errors ?? [];
      if (errors.length > 0) {
        toast.error(
          errors[0].message ?? 'The run settings could not be saved.',
        );
        return;
      }
      toast.success('Run settings saved.');
    } catch {
      toast.error('The run settings could not be saved.');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-light-black dark:text-sublte-white">
        These settings belong to this run only. Changing them does not affect
        the agency or other runs.
      </p>

      {isComputed && (
        <InfoBox variant="default" icon={<InfoIcon />}>
          This run already has a result. Saving discards it and the run goes
          back to Ready to compute.
        </InfoBox>
      )}

      {isBusy && (
        <InfoBox variant="warning" icon={<AlertIcon />}>
          This run is queued or computing. Settings cannot be changed until it
          finishes.
        </InfoBox>
      )}

      <RunSettingsForm disabled={isDisabled} layout="grid" />

      {changes > 0 && (
        <div className="sticky bottom-0 flex items-center justify-between gap-4 rounded-sm border border-design-secondary-black bg-light-gray px-4 py-3 dark:bg-design-gray">
          <div className="flex flex-col">
            <span className="text-sm">
              {changes === 1
                ? '1 unsaved change'
                : `${changes} unsaved changes`}
            </span>
            {isComputed && (
              <span className="text-xs text-yellow">
                Result will be discarded
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DesignButton
              variant="ghost"
              disabled={isSaving}
              onClickHandler={() => dispatch(setSettingsDraft(savedDraft))}
            >
              Discard
            </DesignButton>
            <DesignButton
              variant="primary"
              disabled={isDisabled || isSaving}
              onClickHandler={handleSave}
            >
              {isSaving ? 'Saving…' : 'Save settings'}
            </DesignButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
